/**
 * Invoices Zustand store.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Invoice,
  InvoiceStatus,
  InvoiceSummary,
  ListInvoicesFilters,
  listInvoices,
  listMyCustomerInvoices,
  getInvoice,
  getInvoicesSummary,
  createInvoiceFromOrder,
  updateInvoiceStatus,
} from '../services/invoicesApi';

export type InvoicesListSource = 'admin' | 'customer-self';

interface InvoicesState {
  items: Invoice[];
  total: number;
  loading: boolean;
  saving: boolean;
  error: string | null;
  current: Invoice | null;
  summary: InvoiceSummary | null;
  filters: ListInvoicesFilters;
  source: InvoicesListSource;

  setSource: (source: InvoicesListSource) => void;
  setFilters: (patch: Partial<ListInvoicesFilters>) => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<Invoice>;
  fetchSummary: () => Promise<void>;
  generate: (orderId: number | string) => Promise<Invoice>;
  updateStatus: (id: number | string, status: InvoiceStatus, reason?: string) => Promise<Invoice>;
  reset: () => void;
}

const defaultFilters: ListInvoicesFilters = { limit: 20, offset: 0 };

const initial = {
  items: [] as Invoice[],
  total: 0,
  loading: false,
  saving: false,
  error: null as string | null,
  current: null as Invoice | null,
  summary: null as InvoiceSummary | null,
  filters: { ...defaultFilters },
  source: 'admin' as InvoicesListSource,
};

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useInvoicesStore = create<InvoicesState>()(
  devtools(
    (set, get) => ({
      ...initial,

      setSource: (source) => set({ source }),
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, offset: 0 } }),

      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const fn = get().source === 'customer-self' ? listMyCustomerInvoices : listInvoices;
          const { data, total } = await fn(get().filters);
          set({ items: data, total, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },

      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const inv = await getInvoice(id);
          set({ current: inv, loading: false });
          return inv;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },

      fetchSummary: async () => {
        try {
          const summary = await getInvoicesSummary();
          set({ summary });
        } catch (err) {
          set({ error: toErr(err) });
        }
      },

      generate: async (orderId) => {
        set({ saving: true, error: null });
        try {
          const inv = await createInvoiceFromOrder(orderId);
          set((s) => ({ items: [inv, ...s.items], saving: false }));
          return inv;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      updateStatus: async (id, status, reason) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateInvoiceStatus(id, status, reason);
          set((s) => ({
            items: s.items.map((inv) =>
              String(inv.id) === String(id) ? { ...inv, ...updated } : inv
            ),
            current:
              s.current && String(s.current.id) === String(id)
                ? { ...s.current, ...updated }
                : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },

      reset: () => set({ ...initial, filters: { ...defaultFilters } }),
    }),
    { name: 'invoices-store' }
  )
);
